<template>
    <thead v-if="!mobile" class="table-header">
        <tr>
            <th
                v-for="(header, index) in headers"
                :key="`header-${index}`"
                scope="col"
                :aria-label="getHeaderAriaLabel(header)"
                :class="getHeaderClassList(header)"
                :style="getHeaderStyle(header)"
                :value="header.value"
            >
                <template v-if="header.value === 'data-table-select'">
                    <v-simple-checkbox
                        v-if="!singleSelect"
                        class="app-table__checkbox"
                        color="primaryText"
                        :disabled="loading || !selectable"
                        :value="everyItem"
                        :ripple="false"
                        @input="$emit('toggle-select-all', $event)"
                    />
                </template>
                <template v-else>
                    <app-tooltip :value="header.tooltip" :disabled="!header.tooltip">
                        {{ header.text }}
                        <app-button
                            v-if="!disableSort && header.sortable"
                            x-small
                            icon
                            :disabled="loading"
                            @click="onSortColumn(header)"
                        >
                            <v-icon
                                size="1.4em"
                                :class="getSortIconClass(header)"
                                :color="isBeingSorted(header) ? 'primaryText' : 'grey'"
                            >
                                {{ getIcon('sort', 'fas') }}
                            </v-icon>
                        </app-button>
                    </app-tooltip>

                    <div v-if="showQuickFilters" class="table-header-filter">
                        <transition appear name="fade" mode="out-in">
                            <component
                                :is="fields[header.value].component"
                                v-if="fields[header.value] && header.filterable && !fields[header.value].disabled"
                                v-model="fields[header.value].criteria"
                                edit
                                :placeholder="fields[header.value].enumA11yKey ? $t('select') : $t('search')"
                                :enums="fields[header.value].enum"
                                :enum-a11y-key="fields[header.value].enumA11yKey"
                                :multiple="['in', 'not_in'].includes(fields[header.value].operator)"
                                :filters="fields[header.value].filters"
                                :type="fields[header.value].component === 'EditBoolean' ? 'selection' : null"
                                :disabled="loading || !fields[header.value].operator"
                                :options="fields[header.value].options"
                                hide-details
                                hide-label
                                :default-value="null"
                                clearable
                                small
                                prevent-set-default-value
                                @input="applyFiltersDebounced"
                                @input:object="fields[header.value].data = $event"
                                @clear="applyFilters"
                            />
                            <v-input v-else class="hidden" />
                        </transition>
                    </div>
                </template>
            </th>
        </tr>
    </thead>
</template>

<script>
export default {
    name: 'TableHeader',
    props: {
        value: {
            type: Array,
            default: null
        },
        headers: {
            type: Array,
            required: true
        },
        options: {
            type: Object,
            required: true
        },
        mobile: {
            type: Boolean,
            required: true
        },
        showGroupBy: {
            type: Boolean,
            required: true
        },
        someItems: {
            type: Boolean,
            required: true
        },
        everyItem: {
            type: Boolean,
            required: true
        },
        selectable: {
            type: [Boolean, Number, Function],
            default: false
        },
        singleSelect: {
            type: Boolean,
            required: true
        },
        disableSort: {
            type: Boolean,
            required: true
        },
        loading: {
            type: Boolean,
            required: false
        },
        showQuickFilters: {
            type: Boolean,
            default: false
        },
        filterPanelId: {
            type: String,
            default: null
        },
        filters: {
            type: Object,
            default: () => {}
        }
    },
    data: () => ({
        fields: {},
        applyFiltersDebounced: () => {}
    }),
    computed: {
        maxSelectable() {
            return Number.isInteger(this.selectable) ? Number(this.selectable) : null;
        }
    },
    watch: {
        showQuickFilters: {
            immediate: true,
            handler(showQuickFilters) {
                if (showQuickFilters && this.filterPanelId) {
                    this.setFields();
                }
            }
        },
        filters: {
            handler() {
                if (this.filterPanelId) {
                    this.setFields();
                }
            }
        }
    },
    methods: {
        setup() {
            this.applyFiltersDebounced = this._.debounce(this.applyFilters, 1500);
        },
        setFields() {
            const options = this.$store.getters['filterPanels/options'](this.filterPanelId);

            this.fields = {};

            this.headers.forEach(header => {
                if (header.filterable) {
                    const option = options.find(option => option.column === (header.column || header.value));

                    if (option) {
                        this.fields[header.value] = {
                            ...option,
                            criteria: null,
                            operator: option.operators ? option.operators[0].value : null
                        };
                    } else {
                        this.fields[header.value] = null;
                    }
                }
            });

            const filters = this.$store.getters['filterPanels/filters'](this.filterPanelId);

            filters.forEach(filter => {
                if (this.fields[filter.column]) {
                    this.fields[filter.column].criteria = filter.criteria;
                    this.fields[filter.column].operator = filter.operator;
                }
            });
        },
        getHeaderSortIndex(header) {
            return this.options.sortBy.findIndex(k => k === header.value);
        },
        isBeingSorted(header) {
            return this.getHeaderSortIndex(header) >= 0;
        },
        isDesc(header) {
            return this.options.sortDesc[this.getHeaderSortIndex(header)];
        },
        getHeaderAriaLabel(header) {
            let label = header.text;

            if (this.isBeingSorted(header)) {
                label += ': ' + this.$t('sorted');
            } else {
                label += ': ' + this.$t('not_sorted');
            }

            return label;
        },
        getHeaderClassList(header) {
            let classList = [];

            if (!this.disableSort && header.sortable) {
                classList.push('sortable');
            }

            if (this.isBeingSorted(header)) {
                classList.push('sorted');
            }

            if (header.align) {
                classList.push(`text-${header.align}`);
            }

            if (this._.isArray(header.classList)) {
                classList.push(...header.classList.split(' '));
            }

            return classList.join(' ');
        },
        getHeaderStyle(header) {
            let style = {};

            if (header.width) {
                style.width = header.width;
            }

            if (header.minWidth) {
                style.minWidth = header.minWidth;
            }

            return style;
        },
        onSortColumn(header) {
            if (!this.disableSort && header.sortable && !this.disabled) {
                this.$emit('sort', header.value);
            }
        },
        getSortIconClass(header) {
            let classList = ['app-table__sort-icon'];

            if (this.isBeingSorted(header)) {
                if (this.isDesc(header)) {
                    classList.push('fa-rotate-180');
                }
            }

            return classList.join(' ');
        },
        applyFilters() {
            let filtersArray = this.$store.getters['filterPanels/filters'](this.filterPanelId);

            for (const column in this.fields) {
                const field = this.fields[`${column}`];

                if (field) {
                    const filterIndex = filtersArray.findIndex(filter => filter.column === column);

                    if (filterIndex >= 0 && field.criteria !== null) {
                        filtersArray[`${filterIndex}`].criteria = field.criteria;
                        filtersArray[`${filterIndex}`].operator = field.operator;
                    } else if (field.criteria !== null) {
                        filtersArray.push({
                            column: column,
                            criteria: field.criteria,
                            operator: field.operator,
                            condition: 'and'
                        });
                    } else {
                        filtersArray = filtersArray.filter(filter => filter.column !== column);
                    }
                }
            }

            this.$emit('apply-filters', filtersArray);
        },
        clearFilters() {
            this.$emit('clear-filters');
        }
    }
};
</script>
